import { getDownloadURL } from "@firebase/storage";
import { isEqual } from "lodash";
import { Check } from "lucide-react";
import React, { FocusEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { casePath } from "../features/caseIde/routes";
import { useAddFileToDB } from "../hooks/useAddFileToDB";
import { useDeleteFileFromDB } from "../hooks/useDeleteFileFromDB";
import { useGetFilesFromDB } from "../hooks/useGetFilesFromDB";
import { useGetIndividualData } from "../hooks/useGetIndividualData";
import { useUpdateUser } from "../hooks/useUpdateUser";
import {
  IndividualState,
  updateIndividualAvatar,
  updateIndividualData
} from "../redux/individual/individualSlice";
import { individualSelectors } from "../redux/individual/selectors";
import { DATABASE } from "../types/tables-data";
import AllCountriesSelect from "../components/common/AllCountriesSelect";
import AllStatesSelect from "../components/common/AllStatesSelect";
import { supportedExtensionsString } from "../constans";
import {
  getLegacyOtherPronounsFallback,
  getPronounsSelectValue
} from "../helpers/pronouns";

const steps = [
  { label: "Personal Information" },
  { label: "Contact Information" },
  { label: "Employer Information" }
];

interface TouchedState {
  [key: string]: boolean;
}

const adrTypes = ["Apartment", "Suite", "Floor"];

const PRONOUN_OPTIONS = [
  { value: "he/him", label: "He/Him/His" },
  { value: "she/her", label: "She/Her/Hers" },
  { value: "they/them", label: "They/Them/Theirs" },
  { value: "other", label: "Other" }
];

type ProfileFieldProps = {
  label: string;
  htmlFor?: string;
  error?: string;
  children: React.ReactNode;
};

function ProfileField({ label, htmlFor, error, children }: ProfileFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}

function ProfileStepper({ activeStep }: { activeStep: number }) {
  return (
    <ol className="flex w-full items-center gap-3">
      {steps.map(({ label }, index) => {
        const complete = index < activeStep;
        const active = index === activeStep;
        return (
          <li
            key={label}
            aria-current={active ? "step" : undefined}
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            <span
              className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-full text-xs tabular-nums",
                complete && "bg-accent-soft text-accent-seed",
                active && "bg-primary text-primary-foreground",
                !complete && !active && "bg-fill-2 text-fg-3"
              )}
            >
              {complete ? <Check className="size-3" /> : index + 1}
            </span>
            <span
              className={cn(
                "truncate text-sm",
                active ? "font-medium text-fg" : "text-fg-3"
              )}
            >
              {label}
            </span>
            {index < steps.length - 1 ? (
              <span className="h-px min-w-4 flex-1 bg-line-2" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export const IndividualProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const { onSubmitGetIndividualData, isLoadingGetIndividualData } =
    useGetIndividualData();
  const { onSubmitUpdateUser, isLoadingUpdateUser, onUpdateUserAvatar } =
    useUpdateUser();
  const uid = id ?? useSelector(individualSelectors.selectUid);
  const individualData = useSelector(individualSelectors.selectAll);
  const [formData, setFormData] = useState<IndividualState | null>(
    individualData
  );

  // Form
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  const [touched, setTouched] = useState<TouchedState>({});
  const [isChanged, setIsChanged] = useState(false);

  // Avatar
  const avatarName = useSelector(individualSelectors.selectAvatarName);
  const avatarSrc = useSelector(individualSelectors.selectAvatarSrc);
  const { onSubmitGetFiles, storageFiles } = useGetFilesFromDB();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { onSubmitAddFile, fileRef } = useAddFileToDB();
  const { onSubmitDeleteFile } = useDeleteFileFromDB();

  // Steps
  const [activeStep, setActiveStep] = useState(0);
  const nextStep = () =>
    setActiveStep((step) => Math.min(step + 1, steps.length));
  const prevStep = () => setActiveStep((step) => Math.max(step - 1, 0));
  const reset = () => setActiveStep(0);
  const isLastStep = activeStep === steps.length - 1;
  // const hasCompletedAllSteps = activeStep === steps.length;

  const getImagesFromBD = async () => {
    try {
      await onSubmitGetFiles(`${DATABASE.INDIVIDUALS}/avatars/${uid}/`);
    } catch (e) {
      Sentry.captureException(e);
      dispatch(updateIndividualAvatar({ avatarSrc: "/", avatarName: "" }));
    }
  };

  const uploadImage = async () => {
    if (selectedFile) {
      try {
        await onSubmitAddFile(
          selectedFile,
          `${DATABASE.INDIVIDUALS}/avatars/${uid}/${selectedFile.name}`,
          true
        );
        setSelectedFile(null);
        getImagesFromBD();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  };

  const deleteImage = async () => {
    if (avatarName) {
      try {
        await onSubmitDeleteFile(
          `${DATABASE.INDIVIDUALS}/avatars/${uid}/${avatarName}`
        );
        await onUpdateUserAvatar(uid, {
          avatarSrc: "",
          avatarName: ""
        });
        dispatch(updateIndividualAvatar({ avatarSrc: "/", avatarName: "" }));
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        uploadImage();
      }
    }
  };

  useEffect(() => {
    getImagesFromBD();
  }, []);

  useEffect(() => {
    if (storageFiles.length) {
      storageFiles.forEach((item) => {
        getDownloadURL(item).then((url) => {
          dispatch(
            updateIndividualAvatar({ avatarSrc: url, avatarName: item.name })
          );
          onUpdateUserAvatar(uid, {
            avatarSrc: url,
            avatarName: item.name
          });
        });
      });
    }
    if (storageFiles.length === 0) {
      dispatch(updateIndividualAvatar({ avatarSrc: "/", avatarName: "" }));
    }
  }, [storageFiles]);

  useEffect(() => {
    if (selectedFile && avatarName) {
      deleteImage();
    } else if (selectedFile) {
      uploadImage();
    }
  }, [selectedFile]);

  const handleValidation = () => {
    if (!formData) return false;
    const newErrors: { [key: string]: string } = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name } = e.target;

    // Mark the field as touched when it loses focus
    setTouched({
      ...touched,
      [name]: true
    });
  };

  const handleUpdateData = async () => {
    if (!uid || !formData) return;

    try {
      const updatedValues = {
        // personal info
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        dob: formData.dob,
        countryob: formData.countryob,
        citizenship: formData.citizenship,
        title: formData.title,
        pronouns: formData.pronouns,
        otherPronouns: formData.otherPronouns,
        // contact
        email: formData.email,
        phone: formData.phone,
        // address
        addressLine1: formData.addressLine1,
        state: formData.state,
        city: formData.city,
        zipcode: formData.zipcode,

        // Employer info
        employer_name: formData.employer_name,
        employer_contact_name: formData.employer_contact_name,
        employer_contact_email: formData.employer_contact_email,
        employer_contact_phone: formData.employer_contact_phone,

        employer_addressLine1: formData.employer_addressLine1,
        employer_state: formData.employer_state,
        employer_city: formData.employer_city,
        employer_zipcode: formData.employer_zipcode,

        // usAddress
        usAddressInCareOf: formData.usAddressInCareOf,
        usAddressStreetNumber: formData.usAddressStreetNumber,
        usAddressStreetName: formData.usAddressStreetName,
        usAddressType: formData.usAddressType,
        usAddressAptSteFloor: formData.usAddressAptSteFloor,
        usAddressCityOrTown: formData.usAddressCityOrTown,
        usAddressState: formData.usAddressState,
        usAddressProvince: formData.usAddressProvince,
        usAddressZipCode: formData.usAddressZipCode,
        // foreignAddress
        foreignAddressInCareOf: formData.foreignAddressInCareOf,
        foreignAddressStreetNumber: formData.foreignAddressStreetNumber,
        foreignAddressStreetName: formData.foreignAddressStreetName,
        foreignAddressType: formData.foreignAddressType,
        foreignAddressAptSteFloor: formData.foreignAddressAptSteFloor,
        foreignAddressCityOrTown: formData.foreignAddressCityOrTown,
        foreignAddressState: formData.foreignAddressState,
        foreignAddressProvince: formData.foreignAddressProvince,
        foreignAddressPostalCode: formData.foreignAddressPostalCode,
        visaType: formData.visaType,
        company: formData.company
      };

      await onSubmitUpdateUser(uid, updatedValues);
      dispatch(updateIndividualData(updatedValues));
      await onSubmitGetIndividualData(uid, { logoutOnMissing: false });
      toast.success("Information updated", {
        description: "Information updated.",
        duration: 3000
      });
      navigate(casePath({ individualId: id ?? "" }));
    } catch (e) {
      Sentry.captureException(e);
      toast.error("Error", {
        description: "Error updating information.",
        duration: 3000
      });
    } finally {
      onClose();
    }
  };

  const handleConfirmSubmit = async () => {
    setIsDialogOpen(false);
    setIsSubmitting(true);
    await handleUpdateData();
    setIsSubmitting(false);
    setChangedFields([]);
  };

  useEffect(() => {
    setIsChanged(!isEqual(formData, individualData));
  }, [formData, individualData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, id } = e.target;
    updateField(id, name, value);
  };

  function updateField(id: string, name: string, value: string) {
    setFormData((prevFormData) => {
      if (!prevFormData) return null;
      if (!changedFields.includes(name)) {
        setChangedFields([...changedFields, name]);
      }
      return {
        ...prevFormData,
        [id]: value
      };
    });
  }

  const selectedPronouns = getPronounsSelectValue(formData?.pronouns);
  const otherPronounsValue = getLegacyOtherPronounsFallback(
    formData?.pronouns,
    formData?.otherPronouns
  );

  const fullName = `${formData?.firstName ?? ""} ${
    formData?.middleName ?? ""
  } ${formData?.lastName ?? ""}`;
  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

  return formData ? (
    <div className="h-full overflow-y-auto bg-surface">
      <form className="mx-auto flex w-full max-w-190 flex-col gap-8 px-12 pt-10 pb-24">
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label="Change Photo"
            className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={() => {
              fileRef.current?.click();
            }}
          >
            <Avatar className="size-24 rounded-xl">
              <AvatarImage src={avatarSrc} alt={fullName.trim()} />
              <AvatarFallback className="text-xl">{initials}</AvatarFallback>
            </Avatar>
          </button>
          <PageHeader
            className="flex-1"
            title={fullName.trim() || "Profile"}
            description={formData.email || undefined}
          />
          <input
            type="file"
            ref={fileRef}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                setSelectedFile(e.target.files[0]);
              }
            }}
            accept={supportedExtensionsString}
          />
        </div>

        <ProfileStepper activeStep={activeStep} />

        {steps.map(
          ({ label }, index) =>
            index === activeStep && (
              <div
                key={label}
                className="grid grid-cols-2 gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2"
              >
                {index === 0 && (
                  <>
                    {/* Personal Information */}
                    <ProfileField
                      label="First Name"
                      htmlFor="firstName"
                      error={errors.firstName}
                    >
                      <Input
                        type="text"
                        id="firstName"
                        name="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                        aria-invalid={!!errors.firstName || undefined}
                        required
                      />
                    </ProfileField>
                    <ProfileField label="Middle Name" htmlFor="middleName">
                      <Input
                        type="text"
                        id="middleName"
                        name="Middle Name"
                        value={formData.middleName}
                        onChange={handleChange}
                      />
                    </ProfileField>
                    <ProfileField label="Last Name" htmlFor="lastName">
                      <Input
                        type="text"
                        id="lastName"
                        name="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </ProfileField>
                    <ProfileField
                      label="Date of Birth"
                      htmlFor="dob"
                      error={errors.dob}
                    >
                      <Input
                        type="date"
                        id="dob"
                        name="Date of Birth"
                        value={formData.dob}
                        onChange={handleChange}
                        aria-invalid={!!errors.dob || undefined}
                        required
                      />
                    </ProfileField>
                    <ProfileField label="Country of Birth" error={errors.dob}>
                      <AllCountriesSelect
                        value={formData.countryob}
                        handleChange={handleChange}
                        name="countryob"
                      />
                    </ProfileField>
                    <ProfileField
                      label="Country of Citizenship"
                      error={errors.dob}
                    >
                      <AllCountriesSelect
                        value={formData.citizenship}
                        handleChange={handleChange}
                        name="citizenship"
                      />
                    </ProfileField>
                    <ProfileField label="Pronouns" htmlFor="pronouns">
                      <Select
                        name="pronouns"
                        value={selectedPronouns || undefined}
                        onValueChange={(value) =>
                          updateField("pronouns", "pronouns", value)
                        }
                        required
                      >
                        <SelectTrigger id="pronouns" className="w-full">
                          <SelectValue placeholder="Select Pronouns" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRONOUN_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </ProfileField>
                    {selectedPronouns === "other" ? (
                      <ProfileField
                        label="Other Pronouns"
                        htmlFor="otherPronouns"
                      >
                        <Input
                          type="text"
                          id="otherPronouns"
                          name="otherPronouns"
                          value={otherPronounsValue}
                          onChange={handleChange}
                        />
                      </ProfileField>
                    ) : null}
                    <ProfileField label="Title" htmlFor="title">
                      <Input
                        type="text"
                        id="title"
                        name="Title"
                        value={formData.title || undefined}
                        onChange={handleChange}
                      />
                    </ProfileField>
                  </>
                )}
                {index === 1 && (
                  <>
                    {/* Contact Information */}
                    <ProfileField
                      label="Email"
                      htmlFor="email"
                      error={errors.email}
                    >
                      <Input
                        type="email"
                        id="email"
                        name="Email"
                        value={formData.email}
                        onChange={handleChange}
                        aria-invalid={!!errors.email || undefined}
                        disabled
                      />
                    </ProfileField>
                    <ProfileField
                      label="Phone"
                      htmlFor="phone"
                      error={errors.phone}
                    >
                      <Input
                        type="text"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        aria-invalid={!!errors.phone || undefined}
                      />
                    </ProfileField>
                    <ProfileField
                      label="Address Line"
                      htmlFor="addressLine1"
                      error={errors.addressLine1}
                    >
                      <Input
                        type="text"
                        id="addressLine1"
                        name="addressLine1"
                        value={formData.addressLine1 ?? ""}
                        onChange={handleChange}
                        aria-invalid={!!errors.addressLine1 || undefined}
                      />
                    </ProfileField>
                    <ProfileField
                      label="City"
                      htmlFor="city"
                      error={errors.city}
                    >
                      <Input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city ?? ""}
                        onChange={handleChange}
                        aria-invalid={!!errors.city || undefined}
                      />
                    </ProfileField>
                    <ProfileField
                      label="State"
                      htmlFor="state"
                      error={errors.state}
                    >
                      <AllStatesSelect
                        handleChange={handleChange}
                        value={formData.state ?? ""}
                      />
                    </ProfileField>
                    <ProfileField
                      label="Zip Code"
                      htmlFor="zipcode"
                      error={errors.zipcode}
                    >
                      <Input
                        type="text"
                        id="zipcode"
                        name="zipcode"
                        value={formData.zipcode ?? ""}
                        onChange={handleChange}
                        aria-invalid={!!errors.zipcode || undefined}
                      />
                    </ProfileField>
                  </>
                )}
                {index === 2 && (
                  <>
                    {/* Employer Information */}
                    <ProfileField label="Employer Name" htmlFor="employer_name">
                      <Input
                        name="employer_name"
                        id="employer_name"
                        value={formData.employer_name ?? ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </ProfileField>
                    <ProfileField
                      label="Address Line"
                      htmlFor="employer_addressLine1"
                      error={errors.employer_addressLine1}
                    >
                      <Input
                        type="text"
                        id="employer_addressLine1"
                        name="employer_addressLine1"
                        value={formData.employer_addressLine1 ?? ""}
                        onChange={handleChange}
                        aria-invalid={
                          !!errors.employer_addressLine1 || undefined
                        }
                      />
                    </ProfileField>
                    <ProfileField
                      label="City"
                      htmlFor="employer_city"
                      error={errors.employer_city}
                    >
                      <Input
                        type="text"
                        id="employer_city"
                        name="employer_city"
                        value={formData.employer_city ?? ""}
                        onChange={handleChange}
                        aria-invalid={!!errors.employer_city || undefined}
                      />
                    </ProfileField>
                    <ProfileField
                      label="State"
                      htmlFor="employer_state"
                      error={errors.employer_state}
                    >
                      <AllStatesSelect
                        name="employer_state"
                        handleChange={handleChange}
                        value={formData.employer_state ?? ""}
                      />
                    </ProfileField>
                    <ProfileField
                      label="Zip Code"
                      htmlFor="zipcode"
                      error={errors.zipcode}
                    >
                      <Input
                        type="text"
                        id="zipcode"
                        name="zipcode"
                        value={formData.zipcode ?? ""}
                        onChange={handleChange}
                        aria-invalid={!!errors.zipcode || undefined}
                      />
                    </ProfileField>
                  </>
                )}
              </div>
            )
        )}

        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={reset}>
            Reset
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={activeStep === 0}
              onClick={prevStep}
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={isSubmitting}
              onClick={isLastStep ? onOpen : nextStep}
            >
              {isSubmitting ? <Spinner /> : null}
              {isLastStep ? "Save" : "Next"}
            </Button>
          </div>
        </div>

        <Dialog
          open={isOpen}
          onOpenChange={(open) => (open ? onOpen() : onClose())}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogDescription>
                Do you want to save these changes?
              </DialogDescription>
            </DialogHeader>
            {changedFields.length > 0 && (
              <div className="flex flex-col gap-2 text-base text-fg-2">
                <p>The following information has been changed:</p>
                <ul className="list-disc pl-5">
                  {changedFields.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onClose}
                disabled={isLoadingUpdateUser}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={isLoadingUpdateUser}
                onClick={() => {
                  handleConfirmSubmit();
                  onClose();
                }}
              >
                {isLoadingUpdateUser ? <Spinner /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </form>
    </div>
  ) : null;
};
